import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Bubble } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import WidgetContainer from 'components/Apps/WidgetContainer'
import messages from 'components/Apps/SlaMonitor/translate'
import { fetchCageRateList } from 'reducers/slaMonitor/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import * as COLOR from 'constants/colors'
import {
  getChartsRatio,
  SPECIES_ATLANTIC_SALMON,
  SPECIES_COHO,
} from 'helpers/slaMonitor'

class CageSla extends Component {
  constructor(props) {
    super(props)
    this.state = this.getSlaById(props.activeCage)
  }

  componentDidUpdate(prevProps) {
    const {
      fetchCageRateList,
      activeSite,
      activeDate,
      activeCage,
      isReloadCageList,
    } = this.props

    if (prevProps.activeCage !== activeCage) {
      this.setState(this.getSlaById(activeCage))
    }

    if (
      prevProps.activeDate !== activeDate ||
      prevProps.activeCage !== activeCage ||
      (prevProps.isReloadCageList !== isReloadCageList && isReloadCageList)
    ) {
      fetchCageRateList({
        siteId: activeSite.id,
        locusId: activeCage,
        date: activeDate.format('YYYY-MM-DD'),
      })
    }
  }

  chartData = () => {
    const { cageRateListData } = this.props
    const { species } = this.state
    const values = cageRateListData.get('cageRateList').toArray()
    const data = values.map((entry, index) => {
      const config = this.getSlaConfig(entry, species)

      return {
        label: `Sla ${index}`,
        borderColor: COLOR.TRANSPARENT,
        backgroundColor: !config.type ? config.color : COLOR.TRANSPARENT,
        info: {
          cage: entry.cagename,
          time: moment()
            .set({ hour: entry.hour, minute: entry.minute })
            .format('HH:mm'),
          sla: entry.rate,
          user: entry.username,
          comment: entry.comment !== null ? entry.comment : '',
        },
        data: [
          {
            x: moment()
              .set({ hour: entry.hour, minute: entry.minute })
              .format('YYYY-MM-DD HH:mm'),
            y: entry.rate,
            r: config.size,
          },
        ],
      }
    })

    return {
      datasets: data,
    }
  }

  chartOptions = () => {
    const {
      intl: { formatMessage },
      cageRateListData,
    } = this.props
    const { species } = this.state
    const data = cageRateListData.get('cageRateList').toArray()

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:00',
              },
            },
            offset: data.length === 1,
            ticks: this.getRange(),
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.time),
            },
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: {
              beginAtZero: true,
              max: 5,
              min: 0,
              stepSize: 1,
              callback: v => (v === 5 ? 'X' : v),
            },
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.sla).toUpperCase(),
            },
          },
        ],
      },
      tooltips: {
        displayColors: false,
        callbacks: {
          label: (tooltipItem, data) => {
            const datasetInfo = data.datasets[tooltipItem.datasetIndex].info
            const result = [
              `${formatMessage(messages.cage)}: ${datasetInfo.cage}`,
              `${formatMessage(messages.time)}: ${datasetInfo.time}`,
              `${formatMessage(messages.sla).toUpperCase()}: ${
                datasetInfo.sla
              }`,
              `${formatMessage(messages.user)}: ${datasetInfo.user}`,
            ]

            if (datasetInfo.comment) {
              result.push(
                `${formatMessage(messages.comment)}: ${datasetInfo.comment}`,
              )
            }

            return result
          },
        },
      },
      plugins: {
        datalabels: {
          anchor: 'center',
          color: COLOR.SLA_GRAY,
          font: {
            size: 20,
            weight: 'bold',
          },
          formatter: val => {
            if (species === SPECIES_COHO) {
              return val.y >= 4 ? 'X' : null
            }

            return val.y === 5 ? 'X' : null
          },
        },
      },
    }
  }

  getSlaConfig = (slaEntry, species) => {
    switch (slaEntry.rate) {
      case 1:
        return {
          size: 30,
          color: COLOR.SLA_GREEN,
        }
      case 2:
        return {
          size: 25,
          color: COLOR.SLA_YELLOW,
        }
      case 3:
        return { size: 20, color: COLOR.SLA_RED }
      case 4: {
        if (species !== SPECIES_COHO) {
          return { size: 10, color: COLOR.SLA_PURPLE }
        }
        break
      }
      case 5:
      default:
        return { size: 10, color: COLOR.SLA_GRAY, type: 'x' }
    }
  }

  getSlaById = id => {
    const cageList = this.props.cageList.toArray()
    const defaultState = {
      cageName: 'N/A',
      species: SPECIES_ATLANTIC_SALMON,
    }
    const result = {}

    for (const entry of cageList) {
      if (entry.id === id) {
        result.cageName = entry.name
        result.species = entry.specie
      }
    }

    return result.cageName ? result : defaultState
  }

  getRange = (args = {}) => {
    const data = this.props.cageRateListData.get('cageRateList').toArray()
    const dates = data.map(entry =>
      moment().set({ hour: entry.hour }).startOf('hour'),
    )

    return {
      min: moment.min(dates).subtract(dates.length > 0 ? 1 : 0, 'hour'),
      max: moment.max(dates).add(dates.length > 0 ? 2 : 0, 'hour'),
      ...args,
    }
  }

  render() {
    const {
      cageRateListData,
      intl: { formatMessage },
    } = this.props
    const { cageName } = this.state

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.sla).toUpperCase(),
          end: `${formatMessage(messages.cage)} ${cageName}`,
        }}
        sideTitle={formatMessage(messages.byTheHour)}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-content">
            <CircleLoader loading={cageRateListData.get('loader')} />
            <Bubble
              width={null}
              height={null}
              data={this.chartData()}
              options={this.chartOptions()}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    cageRateListData: state.getIn(['slaMonitor', 'cageRateListData']),
    cageList: state.getIn(['slaMonitor', 'cageList']),
    activeDate: state.getIn(['slaMonitor', 'activeDate']),
    activeSite: state.getIn(['profile', 'profile', 'site']),
    activeCage: state.getIn(['slaMonitor', 'activeCage']),
    isReloadCageList: state.getIn(['slaMonitor', 'isReloadCageList']),
  }),
  {
    fetchCageRateList,
  },
)(injectIntl(CageSla))
